// Turns an intra-deployment comparison result into one grouped distribution
// that every response type shares: a list of categories along the axis, and
// one series per selected Segment.
//
// Compare shows the SAME picture Describe shows, split by Segment, so the
// author reads one chart shape everywhere. What differs by response type is
// only where the categories come from:
//
//   choice / yes_no  — detail.category_counts
//   scale            — detail.ordered_category_counts (authored order kept)
//   text             — detail.derived_categories.category_counts
//   number           — no returned distribution, so the raw selected answers
//                      are binned here over a range SHARED by every Segment.
//                      Per-Segment bins would put different answers under the
//                      same label and make the bars incomparable.
//
// The category axis is the UNION across Segments, so a category that only one
// Segment has still gets a slot and every other Segment shows an honest zero
// rather than the category vanishing.
//
// UI-FREE: no React, no recharts. Safe to unit-test in isolation.

import { sturgesBinCount, binValues } from '../../utils/numericBinning';

export const segmentKey = (mapping) => {
  const ref = mapping?.mapping || mapping || {};
  return `${ref.deployment_id || ''}/${ref.physical_segment_id || ''}`;
};

export const segmentName = (mapping) => mapping?.logical_segment_name || 'Unnamed Segment';

const asObject = (value) => {
  if (!value) return {};
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch (_) { return {}; }
};

const numeric = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  return typeof value === 'number' ? value : Number(String(value).trim());
};

// Why a comparable question produced nothing to draw. The service already
// reports the mechanical reason per cell; repeat it rather than leaving the
// author with an empty frame.
function emptyReason(paired, questionType) {
  const answered = paired.reduce((sum, { cell }) => sum + (Number(cell?.answered_n) || 0), 0);
  const excluded = paired.reduce((sum, { cell }) => sum + (Number(cell?.calculation_ineligible_value_count) || 0), 0);
  if (answered === 0) return 'These Segments have no answers to this question.';
  if (excluded >= answered) {
    const typed = questionType === 'scale' || questionType === 'number'
      ? 'The recorded answers are not stored as numbers, so none of them meet the numeric rule this question is calculated under.'
      : 'None of the recorded answers match this question’s stored answer options.';
    return `${answered} answers were recorded, but all ${excluded} were excluded from the typed calculation. ${typed} Each Segment’s own counts are below.`;
  }
  return 'No comparable distribution was returned for these Segments.';
}

// Category axis order. Numbers sort by value so a 1-10 scale never reads
// 1, 10, 2; everything else keeps first-seen order, which is the order the
// service returned.
function orderCategories(categories) {
  const list = [...categories];
  if (list.length > 0 && list.every(c => Number.isFinite(numeric(c)))) {
    return list.sort((a, b) => numeric(a) - numeric(b));
  }
  return list;
}

// The per-Segment counts for one cell, by response type. Returns null when the
// cell carries no distribution this chart can draw.
function cellCounts(cell, questionType) {
  const detail = asObject(cell?.detail);
  const type = questionType || detail.question_type;
  if (type === 'scale') return asObject(detail.ordered_category_counts);
  if (type === 'yes_no' || type === 'choice') return asObject(detail.category_counts);
  if (type === 'text') {
    const derived = asObject(detail.derived_categories);
    return Object.keys(derived).length ? asObject(derived.category_counts) : null;
  }
  return null;
}

// Raw numeric answers behind a cell. `number` cells return only a descriptive
// summary, so the distribution is rebuilt from the selected observations.
function cellNumbers(cell) {
  const observations = asObject(cell?.payload).selected_observations;
  if (!Array.isArray(observations)) return [];
  return observations.map(o => numeric(o?.raw_answer)).filter(Number.isFinite);
}

/**
 * @returns {{
 *   kind: 'categorical'|'numeric'|'none',
 *   categories: string[],
 *   segments: Array<{key, name, total, counts: Record<string, number>,
 *                    percentages: Record<string, number>, cumulative: number[]}>,
 *   reason?: string
 * }}
 */
export function buildCompareSeries({ mappings = [], cells = [], question, binCount } = {}) {
  const questionID = question?.question_id || question?.id;
  const cellFor = (mapping) => cells.find(cell =>
    segmentKey(cell.mapping) === segmentKey(mapping) &&
    (!questionID || !cell.question_id || cell.question_id === questionID));

  const paired = mappings.map(mapping => ({ mapping, cell: cellFor(mapping) }));
  // The selected question does not always carry its type — the bar question is
  // whatever the author picked, and Compare may know it only as an id. Every
  // returned cell states its own type, so fall back to that rather than
  // silently drawing nothing.
  const questionType = question?.question_type || question?.type
    || paired.map(({ cell }) => asObject(cell?.detail).question_type).find(Boolean);
  if (!paired.length) return { kind: 'none', categories: [], segments: [], reason: 'No Segment results were returned.' };

  // Numeric questions bin over the pooled range so every Segment's bars line up.
  if (questionType === 'number') {
    const perSegment = paired.map(({ mapping, cell }) => ({ mapping, values: cellNumbers(cell) }));
    const pooled = perSegment.flatMap(s => s.values);
    if (!pooled.length) {
      return { kind: 'none', categories: [], segments: [], reason: 'No numeric answers were returned for these Segments.' };
    }
    const bins = binCount || sturgesBinCount(pooled.length);
    const { binnedLabels, binEdges } = binValues(pooled, bins);
    // Every Segment is assigned against the POOLED edges. The top edge is
    // closed so the largest answer lands in the last bin rather than falling
    // off the end.
    const low = binEdges[0];
    const width = (binEdges[binEdges.length - 1] - low) / binnedLabels.length;
    const labelFor = (value) => {
      if (!(width > 0)) return binnedLabels[0];
      const index = Math.min(binnedLabels.length - 1, Math.floor((value - low) / width));
      return binnedLabels[Math.max(0, index)];
    };
    const segments = perSegment.map(({ mapping, values }) => {
      const counts = {};
      binnedLabels.forEach(label => { counts[label] = 0; });
      values.forEach(value => { counts[labelFor(value)] += 1; });
      return finishSegment(mapping, binnedLabels, counts, values.length);
    });
    return { kind: 'numeric', categories: binnedLabels, segments, countLetter: 'n', questionType };
  }

  const countsBySegment = paired.map(({ mapping, cell }) => ({ mapping, counts: cellCounts(cell, questionType) }));
  if (countsBySegment.every(s => s.counts === null)) {
    return {
      kind: 'none', categories: [], segments: [],
      reason: questionType === 'text'
        ? 'These Segments have no categorized answers yet. Categorize this question in Describe to compare them.'
        : 'This response type has no comparable distribution.',
    };
  }

  const union = [];
  const seen = new Set();
  countsBySegment.forEach(({ counts }) => Object.keys(counts || {}).forEach(category => {
    if (!seen.has(category)) { seen.add(category); union.push(category); }
  }));
  const categories = orderCategories(union);
  // No categories at all means there is nothing to draw. Rendering an axis with
  // no bars reads as "these Segments answered nothing", which is usually false:
  // the answers exist and were excluded from the typed calculation. Say which.
  if (!categories.length) {
    return { kind: 'none', categories: [], segments: [], reason: emptyReason(paired, questionType) };
  }

  // `n` is the population ratio in the shell and the answered count on
  // Describe; `c` is the count of categorized responses. A text question's
  // totals are categorized answers, so they are never labelled `n`.
  const countLetter = questionType === 'text' ? 'c' : 'n';

  const segments = countsBySegment.map(({ mapping, counts }) => {
    const filled = {};
    let total = 0;
    categories.forEach(category => {
      const count = Number(counts?.[category]) || 0;
      filled[category] = count;
      total += count;
    });
    return finishSegment(mapping, categories, filled, total);
  });
  return { kind: 'categorical', categories, segments, countLetter, questionType };
}

// Percentages and the running total share one denominator — this Segment's own
// answers — so each Segment's line reaches 100% on its own terms and Segments
// of very different sizes stay comparable.
function finishSegment(mapping, categories, counts, total) {
  const percentages = {};
  const cumulative = [];
  let running = 0;
  categories.forEach(category => {
    const count = counts[category] || 0;
    percentages[category] = total ? (count / total) * 100 : 0;
    running += count;
    cumulative.push(total ? (running / total) * 100 : 0);
  });
  return { key: segmentKey(mapping), name: segmentName(mapping), total, counts, percentages, cumulative };
}

/** Chart rows: one row per category, one field per Segment. */
export function toChartRows(series, { showProportion = false } = {}) {
  return series.categories.map((category, index) => {
    const row = { category };
    series.segments.forEach(segment => {
      row[`count__${segment.key}`] = segment.counts[category] || 0;
      row[`pct__${segment.key}`] = segment.percentages[category] || 0;
      row[`value__${segment.key}`] = showProportion ? (segment.percentages[category] || 0) : (segment.counts[category] || 0);
      row[`cum__${segment.key}`] = segment.cumulative[index];
    });
    return row;
  });
}

/** Per-Segment description of numeric or ordered answers, for the summary box. */
export function segmentStatistics(series) {
  if (series.kind === 'none') return [];
  // A yes/no answer is stored as 1 or 0, so it LOOKS numeric. Its mean is a
  // real statistic — the proportion answering yes — but its median and spread
  // are noise over a two-value column, so the numeric description is decided
  // by the question rather than by the shape of the values.
  const numericAxis = series.questionType !== 'yes_no'
    && series.categories.every(c => Number.isFinite(numeric(c)));
  return series.segments.map(segment => {
    const base = { key: segment.key, name: segment.name, n: segment.total };
    if (!numericAxis || !segment.total) return base;
    const observations = [];
    series.categories.forEach(category => {
      for (let i = 0; i < (segment.counts[category] || 0); i += 1) observations.push(numeric(category));
    });
    observations.sort((a, b) => a - b);
    const n = observations.length;
    const mean = observations.reduce((sum, v) => sum + v, 0) / n;
    const mid = Math.floor(n / 2);
    const median = n % 2 === 0 ? (observations[mid - 1] + observations[mid]) / 2 : observations[mid];
    const sd = Math.sqrt(observations.reduce((acc, v) => acc + (v - mean) ** 2, 0) / n);
    return { ...base, mean, median, sd, min: observations[0], max: observations[n - 1] };
  });
}
